// utils/orderStatus.js
// ─────────────────────────────────────────────────────────────────────────────
// ONE definition of "where is this order in its journey".
//
// Before this file, every payment-return page (Airtel, MoMo, COD, COD-RW, Card,
// Healthcare) carried its OWN copy of the step labels, its OWN status→index map
// and its OWN per-step copy. They drifted: at least one dropped ACCEPTED /
// PICKED_UP / IN_TRANSIT / ARRIVED into an `else` branch that rendered "Order
// Cancelled" for an order that was merely on its way.
//
// Anything that shows order progress reads from here.
// ─────────────────────────────────────────────────────────────────────────────

export const ORDER_STEPS = [
  "Order placed",
  "Order confirmed",
  "Order dispatched",
  "Order delivered",
];

export const CANCELLED_STEP = "Order cancelled";

/**
 * Maps the FULL backend delivery_status enum (models/Orders.js) onto a step
 * index. Statuses missing from this map must fall through to `null` — never to
 * the cancelled branch, which is how "in transit" used to read as "cancelled".
 */
const STEP_BY_STATUS = {
  PROCESSING: 0,
  PENDING_3PL: 0,
  ASSIGNED: 1,
  ACCEPTED: 1,
  PICKED_UP: 2,
  DISPATCHED: 2,
  IN_TRANSIT: 2,
  ARRIVED: 2,
  DELIVERED: 3,
  REJECTED: 4,
  CANCELLED: 4,
};

export const CANCELLED_INDEX = 4;

/** @returns {number|null} step index, or null when the status is unrecognised. */
export const stepIndexForStatus = (status) => {
  if (!status) return null;
  const idx = STEP_BY_STATUS[String(status).toUpperCase()];
  return idx === undefined ? null : idx;
};

export const isCancelled = (status) => stepIndexForStatus(status) === CANCELLED_INDEX;

/** Short line describing what just happened at a given step. */
export const stepDetail = (index) => {
  switch (index) {
    case 0: return "We've received your order and sent it to the vendor.";
    case 1: return "The vendor accepted your order and started preparing it.";
    case 2: return "A rider has picked it up and is on the way to you.";
    case 3: return "Delivered. Thanks for ordering with Lusuku.";
    default: return "This order was cancelled.";
  }
};

/** Steps to render: the cancelled step only appears on a cancelled order. */
export const visibleSteps = (status) =>
  isCancelled(status) ? [...ORDER_STEPS, CANCELLED_STEP] : ORDER_STEPS;
